/**
 * ONU swap: the hardware behind a subscriber is replaced.
 *
 * Not a migration. `onu-migration.ts` is ONE serial arriving on TWO slots — the
 * fibre was repatched and the same unit came up elsewhere, so the row MOVES and
 * the subscriber's platform number moves with it. A swap is the opposite fact:
 * TWO serials on one subscriber's service, because the box died and an engineer
 * put a different one on the drop. The old unit is not somewhere else; it is
 * gone, and the only thing left of it is what we wrote down.
 *
 * Which is why this is RECORDED rather than derived. The obvious derivation —
 * "a removed row and a present row on the same slot" — was measured against the
 * operator's fleet and answers nine of ten swaps. It fails on the tenth, and it
 * fails permanently the moment the old row is deleted, which the deauthorise
 * path does on purpose. So the previous unit's serial, model and name are
 * COPIED into the record as text: the record outlives the row it came from.
 *
 * The two ways a pair is established are kept apart on the record, because they
 * are different strengths of evidence and an operator reading history is
 * entitled to know which one they are looking at:
 *
 *  - `slot` — the new unit took the old one's exact place on the PON
 *    (same port, same ONU index). Physical, and not arguable.
 *  - `subscriber` — the new unit is on the same PON port under the same
 *    subscriber, at a DIFFERENT index. This is a real shape on the live fleet:
 *    an engineer authorising a replacement takes the next free index rather
 *    than reusing the dead unit's, and the old declaration is left on the
 *    chassis. Inferred, therefore labelled as inferred.
 */

import { z } from 'zod';
import { cliActionResultSchema } from './olt.js';

/**
 * How the platform came to know about a swap.
 *
 * `operator` is the one the platform DID rather than noticed: the Swap button
 * on the ONU page (POST /olts/:id/onus/:onuId/swap), which clears the dead
 * unit and re-declares the subscriber's slot with a different serial in one
 * chassis session. The other three are detections after the fact.
 */
export const ONU_SWAP_TRIGGERS = ['sweep', 'authorize', 'backfill', 'operator'] as const;
export type OnuSwapTrigger = (typeof ONU_SWAP_TRIGGERS)[number];

/**
 * What each of those means to somebody reading one row of history. Spelled out
 * here rather than in the tab so the API and the page say the same thing.
 */
export const ONU_SWAP_TRIGGER_LABELS: Record<OnuSwapTrigger, string> = {
  sweep: 'Seen by an OLT sync',
  authorize: 'Recorded as the new ONU was authorised',
  backfill: 'Reconstructed from the inventory',
  operator: 'Swapped from SimpleACS',
};

/**
 * POST /olts/:id/onus/:onuId/swap — replace the hardware behind a subscriber.
 *
 * `newSerial` must be waiting unauthorised on the same chassis; the route
 * refuses anything else by name. `onuType` is the replacement's model as the
 * chassis knows it: a Huawei autofind reports it (and the route uses that when
 * nothing is sent), a ZTE `uncfg` never does, so on ZTE it is required — the
 * wizard prefills the outgoing unit's model, which is the usual like-for-like
 * RMA. Dry-run by default: the subscriber drops while the slot is rebuilt.
 */
export const swapOnuSchema = z.object({
  newSerial: z.string().min(4).max(32),
  onuType: z.string().min(2).max(40).optional(),
  dryRun: z.boolean().default(true),
});
export type SwapOnuInput = z.infer<typeof swapOnuSchema>;

/** What a swap plan says about the two units, beside the commands themselves. */
export const onuSwapPlanSchema = cliActionResultSchema.extend({
  swap: z.object({
    previous: z.object({ serial: z.string(), model: z.string().nullable() }),
    replacement: z.object({
      serial: z.string(),
      model: z.string(),
      /** The PON port the chassis saw the replacement waiting on. */
      seenOnPonPort: z.string(),
      /** False when it was seen on a different port than the outgoing unit's slot. */
      sameDrop: z.boolean(),
    }),
    /** Named things the rebuild carries forward — for the operator to read, not to parse. */
    carried: z.array(z.string()),
    /** Carried lines the replacement model cannot take, dropped by name. */
    dropped: z.array(z.string()),
    /**
     * Whether the TR-069 management stack was composed onto the replacement.
     * Null for an OMCI-managed ONU, where there is none to compose.
     */
    managementRebuilt: z.boolean().nullable(),
  }),
});
export type OnuSwapPlan = z.infer<typeof onuSwapPlanSchema>;

/** The evidence the pair rests on. See the module comment. */
export const ONU_SWAP_MATCHES = ['slot', 'subscriber'] as const;
export type OnuSwapMatch = (typeof ONU_SWAP_MATCHES)[number];

export const ONU_SWAP_MATCH_LABELS: Record<OnuSwapMatch, string> = {
  slot: 'Same PON port and ONU id',
  subscriber: 'Same subscriber on the same PON port',
};

/** One unit of a swap, as much of it as survives its own row. */
export interface OnuSwapUnit {
  /**
   * Null once the ONU row itself is gone — a deauthorise deletes it. The
   * identity below is text and does not go with it, which is the whole point
   * of this table.
   */
  onuId: string | null;
  /** The number an operator says out loud, for the link. Null with the row. */
  number: number | null;
  serial: string;
  model: string | null;
  /** The chassis's name for it, which on this fleet is the subscriber. */
  name: string | null;
  ponPort: string;
  onuIndex: string;
}

export interface OnuSwap {
  id: string;
  /** When the platform LEARNED of the swap, not when the engineer did it. */
  detectedAt: number;
  trigger: OnuSwapTrigger;
  matchedBy: OnuSwapMatch;
  /** Who authorised the replacement, where a person did. */
  actor: string | null;
  oltName: string | null;
  previous: OnuSwapUnit & {
    /**
     * The serial the ACS knew the old unit by, which is NOT always its PON
     * serial — an F670L informs a router serial, a Huawei ONT informs the
     * hex-ASCII of its PON serial. Recorded because it is the only safe handle
     * on the GenieACS row the retired unit left behind.
     */
     deviceSerial: string | null;
    /** The last time the chassis reported it working. */
    lastSeenAt: number | null;
  };
  replacement: OnuSwapUnit;
}
